import { useEffect, useState } from 'react';

import { getQuickAddTemplates } from '../services/quickAddTemplates';
import { normalizeId } from '../utils/ids';
import { isDuplicateProfile } from '../utils/duplicateProfiles';
import { getSessionKey, onSessionChange, useSessionUserId } from '../utils/session';

/**
 * A collection's duplicate profiles, for the Duplicate ▾ on every item
 * surface (utils/duplicateProfiles.js). Those surfaces are many and per row —
 * an items table mounts one action set per row — so the templates are
 * fetched ONCE per collection and shared, the useCollaborators idiom, rather
 * than once per row.
 *
 * Kept fresh two ways: useQuickAddTemplates publishes every list it holds
 * (a fetch, an edit, a promote) through publishQuickAddTemplates, so an
 * author's change shows in the Duplicate menus at once; and an entry older
 * than STALE_MS is re-fetched by the next surface that mounts, so another
 * manager's change arrives within a minute of navigating. A failed load is
 * not remembered — Duplicate simply stays plain.
 *
 * The cache is PER ACCOUNT. The app signs out by removing the token, with no
 * reload (pages/Logout.js), so a module-level list would otherwise serve the
 * next account to sign in on this tab the last one's — the names of their
 * personal profiles in every Duplicate menu. Two guards, both keyed on who
 * is signed in:
 *   - utils/session.js onSessionChange clears the whole cache when the
 *     signed-in account changes (the shared reset every per-user cache uses);
 *   - every entry is stamped with the session it was fetched under
 *     (sessionKey), and an entry of another session is never served — which
 *     also covers a token written straight to storage, before anything has
 *     announced the change.
 *
 * Returns `{ profiles }` — only the templates that ARE duplicate profiles.
 */

const EMPTY = Object.freeze([]);
const STALE_MS = 60 * 1000;
// collectionId -> { at, promise, list, owner }
const cache = new Map();
// collectionId -> Set<(list) => void>
const listeners = new Map();

const onlyProfiles = list => Object.freeze((list || []).filter(isDuplicateProfile));

const sessionKey = getSessionKey; // utils/session.js: who an entry belongs to

/** The collection's entry, if it was fetched under the CURRENT session. */
function entryFor(id) {
	const hit = cache.get(id);
	if (!hit) return null;
	if (hit.owner === sessionKey()) return hit;
	cache.delete(id);
	return null;
}

onSessionChange(() => { cache.clear(); });

function notify(id, list) {
	(listeners.get(id) || new Set()).forEach(fn => fn(list));
}

function load(id) {
	const hit = entryFor(id);
	if (hit && Date.now() - hit.at < STALE_MS) return hit.promise;
	const entry = { at: Date.now(), list: hit?.list || EMPTY, promise: null, owner: sessionKey() };
	// Through a resolved promise, so a service that throws (or, mocked,
	// answers nothing) fails the same quiet way a refused request does.
	entry.promise = Promise.resolve()
		.then(() => getQuickAddTemplates(id))
		.then(list => {
			// Signed out (or in as someone else) while this was in flight:
			// the answer is the previous account's, and nobody may see it.
			if (entry.owner !== sessionKey()) {
				if (cache.get(id) === entry) cache.delete(id);
				return EMPTY;
			}
			entry.list = onlyProfiles(list);
			notify(id, entry.list);
			return entry.list;
		})
		.catch(() => {
			if (cache.get(id) === entry) cache.delete(id);
			return EMPTY;
		});
	cache.set(id, entry);
	return entry.promise;
}

/**
 * The quick add store's list, published: the shared entry becomes this list
 * (it is the freshest the client has) and every mounted Duplicate menu of the
 * collection re-renders with it.
 */
export function publishQuickAddTemplates(collectionId, templates) {
	const id = normalizeId(collectionId);
	if (!id) return;
	const list = onlyProfiles(templates);
	cache.set(id, { at: Date.now(), list, promise: Promise.resolve(list), owner: sessionKey() });
	notify(id, list);
}

/** Test seam: forget every collection. */
export function resetDuplicateProfilesCache() {
	cache.clear();
	listeners.clear();
}

export default function useDuplicateProfiles(collectionId, { enabled = true } = {}) {
	const id = enabled ? normalizeId(collectionId) : '';
	// Re-runs the load below when another account signs in on this tab with
	// the surface still mounted (the cache itself is cleared by
	// onSessionChange above), and — through `who` in the state — never
	// returns a list held for the previous account, not even for the one
	// render before that effect runs.
	const who = useSessionUserId();
	const [state, setState] = useState(() => ({ id, who, profiles: (id && entryFor(id)?.list) || EMPTY }));

	useEffect(() => {
		if (!id) return undefined;
		let active = true;
		const put = list => setState(prev => (prev.id === id && prev.who === who && prev.profiles === list ? prev : { id, who, profiles: list }));
		const listener = list => { if (active) put(list); };
		if (!listeners.has(id)) listeners.set(id, new Set());
		listeners.get(id).add(listener);
		put(entryFor(id)?.list || EMPTY);
		load(id).then(list => { if (active) put(list); });
		return () => {
			active = false;
			listeners.get(id)?.delete(listener);
		};
	}, [id, who]);

	return { profiles: id && state.id === id && state.who === who ? state.profiles : EMPTY };
}
